/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */

import moment from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { getARDetailFilters, getReportData, getTotalCount } from '../../../api/reports';
import { Button, DatePicker, Select } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { ARdetailedFiltersData, generateFiltersExtraData } from '../../../common/hooks/data';
import {
  futureDuration,
  journalTypes,
  pastDuration,
  reportTypes,
} from '../../../constants/reports';
import { components } from '../../../utils/components';
import {
  dateFormat,
  fetchDashboardFilterData,
  fetchMultiSelectData,
  pastDueDaysFilters,
  reports,
} from '../../../utils/constant';
import * as handlers from '../../../utils/handlers';

const retryReportRequest = async (request) => {
  try {
    return await request();
  } catch (error) {
    if (error?.response) throw error;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return request();
  }
};

const getDefaultReportDate = () => moment().startOf('day').toDate();

const FilterItems = ({ setShowTable, page, filters, setFilters }) => {
  const location = useLocation();
  const filterData = useSelector((state) => {
    if (page !== 'journals') return state.filters.dashboardMultiSelectFilters;
    return state.filters.filtersForMultiSelect;
  });
  const defaultFilters = useSelector((state) => state.filters.defaultMultiSelectFilters);
  const filterExtraData = useSelector((state) => state?.filters?.filtersExtraData);

  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(getDefaultReportDate);
  const [fromDateRange, setFromDateRange] = useState('');
  const [toDateRange, setToDateRange] = useState('');
  const [dateRangeDisabled, setDateRangeDisabled] = useState(false);
  const [initialRenderDone, setInitialRenderDone] = useState(false);
  const [durationDisabled, setDurationDisabled] = useState(false);
  const [arFilterTypes, setARFilterTypes] = useState([]);
  const [arFilterSubTypes, setARFilterSubTypes] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [selectSubType, setSelectedSubType] = useState([]);
  const [displaySub, setDisplaySub] = useState([]);
  const [dashboardNavigated, setDashboardNavigated] = useState(false);
  const [agingFilterError, setAgingFilterError] = useState([{ from: false, to: false }]);

  const fetchFilterDataForAPI = (data) => {
    if (page !== 'journals') return fetchDashboardFilterData(data);
    return fetchMultiSelectData(data, defaultFilters);
  };

  useEffect(() => {
    const locState = location?.state;
    try {
      if (
        locState?.filterType &&
        locState?.value &&
        locState?.redirectionFrom === 'dashboard' &&
        dashboardNavigated === false
      ) {
        const reportSelected = locState?.value !== filters?.reportType;
        const typeOfReport = components[locState?.value?.value].type;
        // handle single date reports
        if (reportSelected && typeOfReport === 1) {
          handleFilters(locState?.filterType, locState?.value);
          setDate(new Date(locState?.reportDate));
        } else if (reportSelected && typeOfReport === 2) {
          // handle date range reports
          setFilters({
            ...filters,
            [locState?.filterType]: locState?.value,
            filterType: typeOfReport,
            duration: locState?.duration,
          });
          setDurationDisabled(false);
          setFromDateRange(new Date(locState?.startDate));
          setToDateRange(new Date(locState?.endDate));
        }
        // check all pre-conditions before API call
        if (
          filters?.reportType &&
          filters?.reportType?.value === locState?.value?.value &&
          ((typeOfReport === 1 && date) || (typeOfReport === 2 && fromDateRange && toDateRange))
        ) {
          generateTable();
          setDashboardNavigated(true);
        }
      }
    } catch (_error) {
      handlers.errorHandler(`Error while redirecting to ${locState?.value?.label}`);
    }
  }, [filters]);

  useEffect(() => {
    if (initialRenderDone) {
      ARdetailedFiltersData({
        type: selectedType,
        subType: selectSubType,
      });
      if (selectedType?.length === 0) {
        setDisplaySub(arFilterSubTypes);
      }
    }
  }, [selectedType, selectSubType]);
  useEffect(() => {
    if (initialRenderDone && filters?.reportType?.value === reports.AR_AGING && date) {
      generateTable();
    }
  }, [filterData]);

  const getAndSetARFilters = async () => {
    const isdashboardNav = location?.state?.redirectionFrom === 'dashboard';
    try {
      const res = await getARDetailFilters();
      await setARFilterTypes(res?.data?.data?.category);
      await setARFilterSubTypes(res?.data?.data?.subCategory);
      await setDisplaySub(res?.data?.data?.subCategory);
    } catch (error) {
      handlers.apiErrorHandler(error);
    } finally {
      if (!isdashboardNav || dashboardNavigated) setLoading(false);
    }
  };

  const handleFilters = (filterType, value) => {
    const isdashboardNav = location?.state?.redirectionFrom === 'dashboard';
    if (filterType === 'reportType') {
      const reportFilterType = components[value.value]?.type;
      setFilters({
        ...filters,
        [filterType]: value,
        filterType: reportFilterType,
        duration: null,
      });
      setFromDateRange('');
      setToDateRange('');
      setDate(reportFilterType === 1 ? getDefaultReportDate() : '');
      setInitialRenderDone(false);
      if (value?.value === 'payOffReport') {
        setDurationDisabled(true);
      } else if (value?.value === 'arAgingDetailedReport') {
        if (!isdashboardNav || dashboardNavigated) setLoading(true);
        getAndSetARFilters();
        setSelectedType([]);
        setSelectedSubType([]);
      } else {
        setDurationDisabled(false);
      }

      generateFiltersExtraData({
        journalReportType: value?.value,
        isAgingReport: [
          'arAgingReport',
          'arAgingDetailedReport',
          'pnFuneralTrustAgingSummary',
          'pnFuneralTrustAgingDetailedReport',
        ].includes(value?.value),
      });
    } else if (
      filters?.reportType?.value === 'pastDueReport' &&
      filterType === 'duration' &&
      value.key === 0
    ) {
      const duration = {
        ...value,
        value: {
          from: filters?.duration?.value?.from,
          to: filters?.duration?.value?.to,
        },
      };
      setFilters({ ...filters, duration });
    } else {
      setFilters({ ...filters, [filterType]: value });
    }
  };

  const dateOptionsGenerator = (type) => {
    const options = {
      minDate: '',
      maxDate: '',
      dateFormat: dateFormat.M_D_Y,
    };
    switch (filters?.reportType?.value) {
      case reports.FUTURE_DEED_REPORT: {
        if (type === 'toDate') {
          options.minDate = fromDateRange ? moment(fromDateRange)._d : '';
        }
        return options;
      }
      case reports.INTEREST_AND_FINANCE_BY_CUSTOMER: {
        if (type === 'toDate') {
          options.minDate = fromDateRange ? moment(fromDateRange)._d : '';
        }
        return options;
      }
      case reports.EQUITY_ON_CANCELLED_CONTRACT: {
        if (type === 'toDate') {
          options.minDate = fromDateRange ? moment(fromDateRange)._d : '';
        }
        return options;
      }
      // PayOff Reports Dosent have Duration as Well as Date Filters So This will Be Removed.
      case reports.PAY_OFF_REPORT: {
        if (type === 'fromDate') {
          const min = new Date();
          min.setHours(0, 0, 0, 0);
          min.setDate(min.getDate() + 1);
          options.minDate = min;
        } else {
          options.minDate = fromDateRange ? moment(fromDateRange)._d : '';
        }
        return options;
      }
      default: {
        if (type === 'toDate') {
          options.minDate = fromDateRange ? moment(fromDateRange)._d : '';
        }
        options.maxDate = new Date();
        return options;
      }
    }
  };

  const generateTable = async () => {
    const reportType = filters?.reportType?.value;
    if (reportType === 'payOffReport') {
      setShowTable({
        show: true,
        tableName: reportType,
      });
    } else {
      setLoading(true);
      const reportDate = moment(date).format(dateFormat.YEAR_MONTH_DAY);
      let fromDate = moment(fromDateRange).format(dateFormat.YEAR_MONTH_DAY);
      let toDate = moment(toDateRange).format(dateFormat.YEAR_MONTH_DAY);
      const paginationParamsforList = {
        page: filters?.page,
        limit: filters?.limit,
      };
      let requestParams;
      let dataAvailable = {};
      try {
        if (
          location?.state?.filterType &&
          location?.state?.value &&
          location?.state?.redirectionFrom === 'dashboard'
        ) {
          dataAvailable = fetchFilterDataForAPI(location?.state?.globalFilters);
        } else {
          dataAvailable = fetchFilterDataForAPI(filterData);
        }
        if (reportType === 'PIAExceptionReport') {
          requestParams = {
            ...dataAvailable,
          };
        } else if (reportType === 'arAgingDetailedReport') {
          requestParams = {
            ...(filters?.filterType === 1
              ? { reportDate }
              : { startDate: fromDate, endDate: toDate }),
            ...dataAvailable,
            timezone: moment.tz.guess(),
            types: selectedType?.map((each) => each.id).join(',') || '',
            subTypes: selectSubType?.map((each) => each.id).join(',') || '',
          };
        } else {
          requestParams = {
            ...(filters?.filterType === 1
              ? { reportDate }
              : { startDate: fromDate, endDate: toDate }),
            ...dataAvailable,
            timezone: moment.tz.guess(),
          };
        }

        if (filterExtraData?.journalReportType === 'trustJournal') {
          requestParams.team = '';
          requestParams.reportingManager = '';
        }
        if (reportType === 'pastDueReport') {
          requestParams.startDate = filters?.duration?.value?.from;
          requestParams.endDate = filters?.duration?.value?.to;
          fromDate = filters?.duration?.value?.from;
          toDate = filters?.duration?.value?.to;
        }

        const { data } = await retryReportRequest(() =>
          getReportData(reportType, {
            ...dataAvailable,
            ...requestParams,
            ...(components[reportType]?.pagination && {
              ...paginationParamsforList,
            }),
          }),
        );
        let totalCount = data?.data?.items?.length || 0;
        // remove payoff report condition when count api is ready
        if (components[reportType]?.pagination && reportType !== reports.PAY_OFF_REPORT) {
          try {
            const response = await retryReportRequest(() =>
              getTotalCount(reportType, {
                ...requestParams,
              }),
            );
            totalCount = response?.data?.data?.totalCount || totalCount;
          } catch (error) {
            handlers.apiErrorHandler(error);
          }
        }
        setInitialRenderDone(true);
        setShowTable({
          show: true,
          tableName: reportType,
          tableData: data?.data?.items || [],
          date: reportDate,
          fromDate,
          toDate,
          totalResults: totalCount || 0,
          dynamicHeaders: data?.data?.allTimePeriods || [],
        });
        setLoading(false);
      } catch (error) {
        setLoading(false);
        handlers.apiErrorHandler(error);
      }
    }
  };

  const validateForm = () => {
    if (
      filters?.reportType?.value === 'payOffReport' ||
      filters?.reportType?.value === 'PIAExceptionReport' ||
      filters?.reportType?.value === 'pastDueReport'
    ) {
      return false;
    }
    if (filters?.filterType === 1) {
      return !date;
    }

    return !(fromDateRange && toDateRange);
  };

  const setDateRange = (selectedDuration) => {
    if (selectedDuration === 'custom') {
      setDateRangeDisabled(false);
      setFromDateRange(null);
      setToDateRange(null);
    } else {
      const { startDate, endDate } = handlers.getStartDateEndDate(selectedDuration);

      setFromDateRange(startDate);
      setToDateRange(endDate);
      setDateRangeDisabled(true);
    }
  };

  const durationOptions = () => {
    if (
      filters?.reportType?.value === reports.FUTURE_DEED_REPORT ||
      filters?.reportType?.value === reports.PAY_OFF_REPORT ||
      filters?.reportType?.value === reports.INTEREST_AND_FINANCE_BY_CUSTOMER
    ) {
      return futureDuration;
    }
    if (filters?.reportType?.value === reports.UNPAID_TRANSACTION_REPORT) {
      return pastDuration.filter((option) => option?.value !== 'today');
    }
    if (filters?.reportType?.value === reports.OVER_DUE_EXCEPTION_REPORT) {
      return pastDueDaysFilters;
    }
    return pastDuration;
  };

  const customAgingDaysOptionsChange = (type, value) => {
    const tempFilters = { ...filters };
    let _value = parseInt(value, 10);
    if (Number.isNaN(_value) || _value < 0) _value = '';
    if (
      typeof _value === 'number' &&
      (type === 'from'
        ? typeof filters.duration.value.to === 'number'
        : typeof filters.duration.value.from === 'number')
    ) {
      if (type === 'from' && _value > filters.duration.value.to) {
        setAgingFilterError({ from: true, to: false });
      } else if (type === 'to' && _value < filters.duration.value.from) {
        setAgingFilterError({ from: false, to: true });
      } else {
        setAgingFilterError({ from: false, to: false });
      }
    } else {
      setAgingFilterError({ from: false, to: false });
    }
    tempFilters.duration.value[type] = _value;
    setFilters(tempFilters);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <Row className="generate-card">
      <h3 className="generate-report-head-1">Generate report</h3>
      <Col lg={6} className="generate-card-item">
        <Row>
          <Col lg={filters?.filterType === 1 ? 9 : 8} className="generate-label generate-label-1">
            <Select
              required
              name="reports"
              formInput={false}
              classNamePrefix="select-dropdown"
              placeholder="Select Report"
              label={page === 'journals' ? 'Choose a journal' : 'Choose a report'}
              onChange={(val) => {
                handleFilters('reportType', val);
              }}
              value={filters.reportType}
              options={page === 'journals' ? journalTypes : reportTypes}
              size="lg"
            />
            {filters?.reportType?.value === reports.AR_AGING_DETAIL_REPORT && (
              <div className="detailed-ar-dropdowns">
                <Select
                  className="type-dropdown"
                  isMulti
                  name="type"
                  formInput={false}
                  classNamePrefix="select-dropdown"
                  placeholder="Overall"
                  label="TYPE"
                  onChange={(val) => {
                    setSelectedType(val);
                    if (!val || (val && val.length <= 0)) {
                      setDisplaySub(arFilterSubTypes);
                      setSelectedType([]);
                      setSelectedSubType([]);
                    } else {
                      const selectTypes = val?.map((type) => type.id);
                      setDisplaySub(
                        arFilterSubTypes?.filter((each) => selectTypes?.includes(each.category)),
                      );
                      setSelectedSubType(
                        selectSubType.filter((subType) => selectTypes?.includes(subType.category)),
                      );
                    }
                  }}
                  value={selectedType}
                  options={arFilterTypes}
                  size="lg"
                />
              </div>
            )}
          </Col>
          {filters?.filterType === 2 && (
            <Col lg={4} className="Duration-label">
              <Select
                required={filters?.reportType?.value !== reports.OVER_DUE_EXCEPTION_REPORT}
                name="duration"
                formInput={false}
                classNamePrefix="select-dropdown"
                placeholder="Duration"
                label="Duration Days"
                onChange={(val) => {
                  handleFilters('duration', val);
                  setDateRange(val.value);
                }}
                value={filters.duration}
                options={durationOptions()}
                size="lg"
                disabled={durationDisabled}
              />
            </Col>
          )}
        </Row>
      </Col>
      <Col lg={6}>
        <Row className="generate-card-item1">
          {filters?.filterType === 1 && (
            <Col lg={4} className="date-picker-g">
              <DatePicker
                required
                name="enter date"
                // #TODO Need to recheck if this className is being applied
                className="datepicker-wrapper"
                formInput={false}
                placeholder="Date"
                label="Enter Date"
                value={date}
                onChange={([selectedDate]) => {
                  setDate(selectedDate);
                }}
                options={dateOptionsGenerator('date')}
                kind="md"
              />
            </Col>
          )}
          {filters?.reportType?.value !== reports.OVER_DUE_EXCEPTION_REPORT &&
            filters?.filterType === 2 && (
              <>
                <Col lg={4} className="date-picker-g">
                  <DatePicker
                    required={filters?.duration?.value === 'custom'}
                    formInput={false}
                    name="from date"
                    placeholder="Date"
                    value={fromDateRange}
                    onChange={([selectedDate]) => {
                      setFromDateRange('');
                      setToDateRange('');
                      setFromDateRange(selectedDate);
                    }}
                    options={dateOptionsGenerator('fromDate')}
                    label="From"
                    kind="md"
                    disabled={!filters?.duration?.value ? true : dateRangeDisabled}
                  />
                </Col>
                <Col lg={4} className="date-picker-g">
                  <DatePicker
                    required={filters?.duration?.value === 'custom'}
                    name="to date"
                    formInput={false}
                    placeholder="Date"
                    value={toDateRange}
                    onChange={([selectedDate]) => {
                      setToDateRange(selectedDate);
                    }}
                    options={dateOptionsGenerator('toDate')}
                    label="To"
                    kind="md"
                    disabled={
                      !filters?.duration?.value || !fromDateRange ? true : dateRangeDisabled
                    }
                  />
                </Col>
              </>
            )}
          {filters?.reportType?.value === reports.OVER_DUE_EXCEPTION_REPORT &&
            filters?.duration &&
            filters?.duration?.key === 0 && (
              <>
                <Col lg={4} className="custom-aging-days-inputs">
                  <div className="input-field">
                    <label htmlFor="from-days">From</label>
                    <br />
                    <input
                      className="input"
                      type="number"
                      name="from-days"
                      min="0"
                      placeholder="From"
                      value={filters?.duration?.value?.from}
                      onChange={(e) => {
                        customAgingDaysOptionsChange('from', e.target.value);
                      }}
                    />
                    {agingFilterError.from && (
                      <p className="error">Please enter a valid filter input</p>
                    )}
                  </div>
                </Col>
                <Col lg={4} className="custom-aging-days-inputs">
                  <div className="input-field">
                    <label htmlFor="to-days">To</label>
                    <br />
                    <input
                      className="input"
                      type="number"
                      name="to-days"
                      placeholder="To"
                      min="0"
                      value={filters?.duration?.value?.to}
                      onChange={(e) => {
                        customAgingDaysOptionsChange('to', e.target.value);
                      }}
                    />
                    {agingFilterError.to && (
                      <p className="error">Please enter a valid filter input</p>
                    )}
                  </div>
                </Col>
              </>
            )}
          <Col
            lg={filters?.filterType === 1 ? 8 : 4}
            className={filters?.filterType === 1 ? 'generate-button' : 'generate-button-1'}
          >
            <Button onClick={generateTable} disabled={validateForm()} color="primary">
              Generate
            </Button>
          </Col>
        </Row>
        <Row className="generate-card-item sub-type-align">
          {filters?.reportType?.value === reports.AR_AGING_DETAIL_REPORT && (
            <Col lg={9} className="generate-label generate-label-1">
              <div className="detailed-ar-dropdowns pagination-araging-report">
                <Select
                  isMulti
                  name="subType"
                  formInput={false}
                  className="subtype-dropdown"
                  classNamePrefix="select-dropdown"
                  placeholder="Overall"
                  label="SUB-TYPE"
                  onChange={(val) => {
                    setSelectedSubType(val);
                  }}
                  value={selectSubType}
                  options={displaySub}
                  size="lg"
                />
              </div>
            </Col>
          )}
        </Row>
      </Col>
    </Row>
  );
};
export default FilterItems;
